import { createContext, useContext, useMemo, useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";

const DateRangeContext = createContext(null);

export const PRESETS = [
  { key: "7d", label: "7 days", days: 7 },
  { key: "30d", label: "30 days", days: 30 },
  { key: "90d", label: "90 days", days: 90 },
  { key: "mtd", label: "Month", days: null },
  { key: "ytd", label: "Year", days: null },
];

const VALID_PRESETS = new Set(["7d", "30d", "90d", "mtd", "ytd", "custom"]);

function computeRange(presetKey) {
  const end = new Date();
  let start = new Date();
  if (presetKey === "mtd") {
    start = new Date(end.getFullYear(), end.getMonth(), 1);
  } else if (presetKey === "ytd") {
    start = new Date(end.getFullYear(), 0, 1);
  } else {
    const p = PRESETS.find((x) => x.key === presetKey) || PRESETS[1];
    start = new Date(end.getTime() - (p.days - 1) * 86400000);
    start.setHours(0, 0, 0, 0);
  }
  return { start: start.toISOString(), end: end.toISOString() };
}

/** Convert YYYY-MM-DD URL param to a full ISO string. */
function dateParamToISO(dateStr, isEnd = false) {
  if (!dateStr) return null;
  const [y, m, d] = dateStr.split("-").map(Number);
  const date = new Date(y, m - 1, d);
  if (isEnd) date.setHours(23, 59, 59, 999);
  else date.setHours(0, 0, 0, 0);
  return date.toISOString();
}

/** Convert a full ISO string to YYYY-MM-DD for clean URL storage. */
function isoToDateParam(iso) {
  if (!iso) return null;
  return iso.split("T")[0];
}

function getStateFromSearchParams(searchParams) {
  const urlPreset = searchParams.get("preset");
  const preset = VALID_PRESETS.has(urlPreset) ? urlPreset : null;
  let customRange = null;
  if (preset === "custom") {
    const start = dateParamToISO(searchParams.get("start"), false);
    const end = dateParamToISO(searchParams.get("end"), true);
    if (start && end) customRange = { start, end };
  }
  return { preset, customRange };
}

export function DateRangeProvider({ children }) {
  const [searchParams, setSearchParams] = useSearchParams();

  const [preset, setPresetInternal] = useState(() => {
    const { preset } = getStateFromSearchParams(searchParams);
    return preset || "30d";
  });

  const [customRange, setCustomRange] = useState(() => {
    const { customRange } = getStateFromSearchParams(searchParams);
    return customRange;
  });

  // Sync FROM URL when the user navigates back/forward
  useEffect(() => {
    const { preset: urlPreset, customRange: urlCustomRange } = getStateFromSearchParams(searchParams);
    const resolved = urlPreset || "30d";
    setPresetInternal(resolved);
    if (resolved === "custom" && urlCustomRange) {
      setCustomRange(urlCustomRange);
    } else if (resolved !== "custom") {
      setCustomRange(null);
    }
  }, [searchParams]);

  const range = useMemo(() => {
    if (preset === "custom" && customRange) return customRange;
    return computeRange(preset);
  }, [preset, customRange]);

  const setPreset = (key) => {
    setPresetInternal(key);
    if (key !== "custom") setCustomRange(null);
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (key === "30d") {
          next.delete("preset"); // default — keep URL clean
        } else {
          next.set("preset", key);
        }
        next.delete("start");
        next.delete("end");
        return next;
      },
      { replace: false } // push to history so back/forward works
    );
  };

  const setCustom = (start, end) => {
    setCustomRange({ start, end });
    setPresetInternal("custom");
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set("preset", "custom");
        next.set("start", isoToDateParam(start));
        next.set("end", isoToDateParam(end));
        return next;
      },
      { replace: false } // push to history so back/forward works
    );
  };

  const value = { preset, setPreset, setCustom, range };
  return <DateRangeContext.Provider value={value}>{children}</DateRangeContext.Provider>;
}

export const useDateRange = () => useContext(DateRangeContext);
